import { useQuery } from '@tanstack/react-query'

import useAppStore from '@/store/useAppStore'
import useGetPeriodColumns from '@/services/api/cash-flow/useGetPeriodColumns'
import useGetTransactions from '@/services/api/cash-flow/transaction/useGetTransactions'
import useGetVaults from '@/services/api/vault/useGetVaults'

export default function useGetVaultClosingBalanceByOffset({
  vaultId,
  offset,
}: {
  offset: number
  vaultId?: number
}) {
  const viewPeriodType = useAppStore((state) => state.periodType)
  const viewOffset = useAppStore((state) => state.offset)
  const viewNumberOfPeriods = useAppStore((state) => state.numberOfPeriods)

  const { promise: promisePeriod } = useGetPeriodColumns({
    offset: viewOffset,
    numberOfPeriods: viewNumberOfPeriods,
    periodType: viewPeriodType,
  })

  const { promise: promiseTransactions } = useGetTransactions({
    offset: viewOffset,
    numberOfPeriods: viewNumberOfPeriods,
    periodType: viewPeriodType,
  })

  const { promise: promiseVaults } = useGetVaults({
    offset: viewOffset,
    numberOfPeriods: viewNumberOfPeriods,
    periodType: viewPeriodType,
  })

  const fetchData = async () => {
    const [periods, transactions, vaults] = await Promise.all([
      promisePeriod,
      promiseTransactions,
      promiseVaults,
    ])

    const _vaultBalance = []

    const rows = [...vaults]
    const allOffsets = periods?.map((col) => col.offset) || []
    rows.forEach((item) => {
      if (item.is_group) {
        item.vaults.forEach((vault) => {
          const { id, vault_group_id } = vault
          allOffsets.forEach((offset) => {
            const sum = transactions.reduce(
              (acc, transaction) => {
                if (
                  transaction.offset === offset &&
                  transaction.vault_id === id
                ) {
                  return acc + transaction.signed_amount
                }
                return acc
              },
              vault.offset === offset ? vault.initial_balance : 0,
            )
            const existingVault = _vaultBalance.find(
              (v) => v.vaultId === id && v.offset === offset,
            )
            if (existingVault) {
              existingVault.amount += sum
            } else {
              _vaultBalance.push({
                vaultId: id,
                offset,
                amount: sum,
                vaultGroupId: vault_group_id,
                initialBalance: vault.initial_balance,
              })
            }
          })
        })
      } else {
        const { id, vault_group_id } = item
        allOffsets.forEach((offset) => {
          const sum = transactions.reduce(
            (acc, transaction) => {
              if (
                transaction.offset === offset &&
                transaction.vault_id === id
              ) {
                return acc + transaction.signed_amount
              }
              return acc
            },
            item.offset === offset ? item.initial_balance : 0,
          )
          const existingVault = _vaultBalance.find(
            (v) => v.vaultId === id && v.offset === offset,
          )
          if (existingVault) {
            existingVault.amount += sum
          } else {
            _vaultBalance.push({
              vaultId: id,
              offset,
              amount: sum,
              vaultGroupId: vault_group_id,
              initialBalance: item.initial_balance,
            })
          }
        })
      }
    })

    const cols = periods?.map((col) => col.offset) || []
    let index = cols.indexOf(offset)
    const offsets = cols.slice(0, index + 1)

    // The user is trying to get the opening balance of the first period
    if (Number(periods[0]?.offset) === Number(offset) + 1) {
      const res = _vaultBalance?.find(
        (x) => x.vaultId === vaultId && Number(x.offset) === Number(offset) + 1,
      ).initialBalance

      return res ?? 0
    }

    return _vaultBalance
      ?.filter((x) => x.vaultId === vaultId && offsets.includes(x.offset))
      ?.reduce((acc, item) => {
        return acc + item.amount
      }, 0)
  }

  return useQuery({
    queryKey: [
      'closing-balance',
      { vaultId, offset, viewNumberOfPeriods, viewPeriodType, viewOffset },
    ],
    queryFn: fetchData,
    enabled: offset !== undefined && vaultId !== undefined,
  })
}
